import { SearchFilterProperties } from "helpers/services/mixpanel/events";
import { isEqual } from "lodash";
import { Filters } from "store/slices/search";
import { stringifyBoolean, stringifyNumber } from "./formatters";
import { getQueryParamsFromCountries } from "./queryParams/countries";
import { getQueryParamsFromDomains } from "./queryParams/domains";
import { getQueryParamsFromStudyTypes } from "./queryParams/studyTypes";

export function countFilters(filters: Filters): number {
  return Object.entries(filters).reduce((count, [key, value]) => {
    if (key === "durationUnit") return count; // Don't count the unit as a filter.

    if (Array.isArray(value)) {
      return count + value.length; // Count each item individually
    }
    if (typeof value === "boolean") {
      return value ? count + 1 : count;
    }
    if (typeof value === "number") {
      return value !== 0 ? count + 1 : count;
    }
    return value !== undefined && value !== null ? count + 1 : count;
  }, 0);
}

export function areFiltersEqual(filtersA: Filters, filtersB: Filters): boolean {
  const normalize = (obj: Filters) =>
    Object.fromEntries(
      Object.entries(obj).filter(([key, value]) => {
        if (!value) return false; // Treat falsy values as unset (0, "", etc)
        if (Array.isArray(value) && value.length === 0) return false; // treat empty arrays as unset
        if (key === "durationUnit" && value === "day") return false; // treat default unit as unset
        return true;
      })
    );

  return isEqual(normalize(filtersA), normalize(filtersB));
}

/**
 * Converts a Filters object to SearchFilterProperties for analytics tracking.
 * This properly converts arrays to comma-separated strings, booleans to strings,
 * and maps property names to match the analytics schema.
 */
export const filtersToAnalyticsProperties = (
  filters: Filters
): SearchFilterProperties => {
  const analyticsFilters = {
    // Array filters converted to comma-separated strings
    studyTypes: getQueryParamsFromStudyTypes(filters.studyTypes),
    fieldsOfStudy: getQueryParamsFromDomains(filters.domains),
    countriesOfStudy: getQueryParamsFromCountries(filters.countries),

    // Boolean filters converted to strings
    openAccess: stringifyBoolean(filters.openAccess),
    hasValidChatPdf: stringifyBoolean(filters.hasValidChatPdf),
    filterControlledStudies: stringifyBoolean(filters.filterControlledStudies),
    filterHumanStudies: stringifyBoolean(filters.filterHumanStudies),

    // Number filters converted to strings (exclude 0 values for analytics)
    citationsMin: stringifyNumber(filters.citationsMin),
    sampleSizeMin: stringifyNumber(filters.sampleSizeMin),
    sampleSizeMax: stringifyNumber(filters.sampleSizeMax),
    sjrBestQuartileMin: stringifyNumber(filters.sjrBestQuartileMin),
    sjrBestQuartileMax: stringifyNumber(filters.sjrBestQuartileMax),
    yearMin: stringifyNumber(filters.yearMin),
    yearMax: stringifyNumber(filters.yearMax),
    durationMin: stringifyNumber(filters.durationMin),
    durationMax: stringifyNumber(filters.durationMax),
  };

  return analyticsFilters;
};
